import { t } from '@/utils/i18n'
import { useState, useMemo, useEffect } from 'react'
import { useMount, useSetState } from 'ahooks'
import {
  Popup,
  CascadePicker,
  Input,
  Toast,
  TextArea,
  Button,
} from 'antd-mobile'
import { RightOutline } from 'antd-mobile-icons'
import useCorpInfoStore from '@/stores/corp-info'
import { apis, utils, common, constant, storageUtil } from '@/utils'
import { CustomerAddress } from '@/pages/Order/types'
import { getI18nLang } from '@/utils/i18n'
import type { OptionItem } from '@/types/interfaces'
import styles from './index.module.less'

const { getLocalStorage } = storageUtil
const { basicApi, crmApi } = apis
const {
  regExp: { PHONE_REG }
} = constant
const { transListToTree } = common
const { to } = utils

const initForm = {
  name: '',
  phone: '',
  area: [],
  areaValues: [],
  address: '',
  addressId: '',
  totalAddress: '',
}

interface IProps {
  visible: boolean
  pfUserId: string
  initData?: { name: string, phone: string }
  onCancel?: () => void
  onOk: (userAddress: CustomerAddress) => void
  onChange?: (userAddress: CustomerAddress) => void
}

function DeliveryAddress(props: IProps) {
  const { visible, pfUserId, initData, onCancel, onOk } = props
  const [showAreaPick, setShowAreaPick] = useState(false)
  const [loading, setLoading] = useState(false)
  const [areaOpts, setAreaOpts] = useState<OptionItem[]>([])
  const [form, setForm] = useSetState<CustomerAddress>(initForm)

  // 省市区地址
  const areaLabel = useMemo(() => {
    const { area } = form
    if (!area?.length) return ''
    // 如果语言是英文，需要倒序
    const lang = getI18nLang()
    if (['en', 'vi'].includes(lang)) {
      return area.map((item: OptionItem) => item.label).reverse().join(' ')
    } else {
      return area.map((item: OptionItem) => item.label).join(' ')
    }
  }, [form])

  const zoneNumber = getLocalStorage('zoneNumber')

  const { corpInfo } = useCorpInfoStore()

  useEffect(() => {
    const totalAddress = areaLabel + form.address
    setForm({ totalAddress })
  }, [areaLabel, form.address])

  useMount(() => {
    getPfUserAddress()
    getAreaList()
  })

  // 获取用户的地址信息
  async function getPfUserAddress() {
    const [err, res] = await to(
      crmApi.getPfUserAddress({
        id: pfUserId,
      })
    )

    if (err || !res) {
      Toast.show(err?.msg || t('查询地址户信息'))
      return
    }

    const resData = res.data || {}
    const {
      name,
      phone,
      provinceName,
      addressId,
      provinceId,
      cityName,
      cityId,
      districtName,
      districtId,
      address,
    } = resData
    const area: OptionItem[] = [
      {
        label: provinceName,
        value: provinceId,
      },
      {
        label: cityName,
        value: cityId,
      },
      {
        label: districtName,
        value: districtId,
      },
    ]

    const areaValues: string[] = [provinceId, cityId, districtId]
    const totalAddress = provinceName + cityName + districtName + address
    const userInfo: CustomerAddress = {
      name,
      phone,
      area,
      areaValues,
      address,
      totalAddress,
      addressId,
    }
    // 如果名字和手机号没有值，默认读个人资料的值
    if (!name && initData?.name) {
      userInfo.name = initData?.name
    }
    if (!phone && initData?.phone) {
      userInfo.phone = initData?.phone
    }
    setForm(userInfo)
    onOk?.(userInfo)
  }

  // 获取省市区
  async function getAreaList() {
    const [err, res] = await to(
      basicApi.getAreaList({
        countryId: corpInfo.countryId,
      })
    )

    if (err || !res) {
      Toast.show(err?.msg || t('获取地址户信息失败'))
      return
    }

    const resData = res.data || {}
    handleAreaData(resData)
  }

  // 把省市区数据处理成树形结构
  function handleAreaData(resData: any) {
    const provinces = (resData.provinces || []).map((item: any) => ({
      label: item.provinceName,
      value: item.provinceId,
      parentId: item.countryId,
    }))
    const citys = (resData.citys || []).map((item: any) => ({
      label: item.cityName,
      value: item.cityId,
      parentId: item.provinceId,
    }))
    const districts = (resData.districts || []).map((item: any) => ({
      label: item.districtName,
      value: item.districtId,
      parentId: item.cityId,
    }))
    const total = [...provinces, ...citys, ...districts]

    const areaTree = transListToTree(total, 'value')
    setAreaOpts(areaTree)
  }

  function onAreaConfirm(_: any, { items: area }: any) {
    const areaData = area.filter(Boolean)
    setForm({ area: areaData })
  }

  // 校验收货地址
  function checkOrderAddress() {
    const { name, phone, area, address } = form
    if (!name) {
      Toast.show(t('请填写姓名'))
      return false
    }
    if (!phone) {
      Toast.show(t('请填写联系电话'))
      return false
    }
    if (!area?.length || !address) {
      Toast.show(t('请填写收货地址'))
      return false
    }
    return true
  }

  // 手机号码变化
  function onPhoneBlur() {
    if (!PHONE_REG.test(form.phone)) {
      Toast.show(t('请输入正确的手机号码'))
      setForm({ phone: '' })
    }
  }

  // 保存，提交订单
  async function onSave() {
    if (!checkOrderAddress()) return
    const { name, phone, area, address, addressId } = form
    const areaIds = area?.map((item: any) => item.value)
    const areaNames = area?.map((item: any) => item.label)
    const [provinceId = '', cityId = '', districtId = ''] = areaIds || []
    const [provinceName = '', cityName = '', districtName = ''] =
      areaNames || []
    setLoading(true)
    const [err, res] = await to(
      crmApi.updatePfUserAddress({
        pfUserId,
        addressId,
        name,
        phone,
        provinceId,
        provinceName,
        cityId,
        cityName,
        districtId,
        districtName,
        address,
      })
    )
    setLoading(false)

    if (err || !res) {
      Toast.show(err?.msg || t('保存收货地址失败'))
      return
    }

    onOk(form)
  }

  return (
    <Popup
      visible={visible}
      onMaskClick={onCancel}
      showCloseButton={true}
      onClose={onCancel}
    >
      <div className={styles['header']}>
        <div className={styles['title']}>{t('收货地址')}</div>
      </div>
      <div className={styles['content']}>
        <div className={styles['form-item']}>
          <div className={styles['form-label']}>{t('名字')}</div>
          <Input
            className={styles['form-content']}
            placeholder={t('请输入名字')}
            value={form.name}
            maxLength={ 40 }
            onChange={(val) => setForm({ name: val })}
          />
        </div>
        <div className={styles['form-item']}>
          <div className={styles['form-label']}>{t('手机号')}</div>
          <div className={ styles['form-content-wrap'] }>
            {
              zoneNumber && (
                <span className={ styles['zone-number'] }>{ zoneNumber }</span>
              )
            }
            <Input
              className={styles['form-content']}
              placeholder={t('请输入手机号')}
              value={form.phone}
              maxLength={ 18 }
              onChange={(val) => setForm({ phone: val })}
              onBlur={onPhoneBlur}
            />
          </div>
        </div>
        <div className={styles['form-item']}>
          <div className={styles['form-label']}>{t('地址')}</div>
          <div
            className={`${styles['form-content']} ${styles['flex-box']}`}
            onClick={() => setShowAreaPick(true)}
          >
            <div className={`${areaLabel ? '' : styles['tip-text']}`}>
              {areaLabel || t('请选择地区')}
            </div>
            <RightOutline />
          </div>
        </div>
        <div className={styles['form-item']}>
          <TextArea
            className={styles['form-content']}
            placeholder={t('请输入详细地址')}
            autoSize={{ minRows: 2 }}
            maxLength={ 200 }
            value={form.address}
            onChange={(val) => setForm({ address: val })}
          />
        </div>
      </div>
      <div className={styles['footer']}>
        <Button
          className={styles['save-btn']}
          color="primary"
          loading={loading}
          onClick={onSave}
        >
          {t('保存')}
        </Button>
      </div>

      <CascadePicker
        value={form.areaValues}
        visible={showAreaPick}
        options={areaOpts}
        onClose={() => setShowAreaPick(false)}
        onConfirm={onAreaConfirm}
      />
    </Popup>
  )
}

export default DeliveryAddress
